import { openIndexedDb, requestToPromise, transactionDone } from '../utils/idb';
import type { LocalHistoryCiphertext } from '../historySecrets/types';
import type { DeviceRecord } from '@shared/types';
import {
  canUseNativeChatEpochKeyStore,
  canUseNativeChatStorage,
  deleteNativeChatEpochKey,
  getNativeChatEpochKey,
  nativeChatStorage,
  saveNativeChatEpochKey
} from '../utils/webviewBridge';

export type StoredGroupChat = {
  localIdentityId?: string;
  serverUrl?: string;
  chatId: string;
  title: string;
  ownerIdentityId: string;
  createdAt: number;
  updatedAt: number;
  lastMessageAt: number | null;
  lastMessagePreview: string | null;
  participantCount: number;
  unreadCount: number;
  muted: boolean;
  keyEpoch: number;
};

export type StoredGroupMessage = {
  messageId: string;
  chatId: string;
  kind: 'user' | 'system';
  senderIdentityId: string | null;
  ciphertext: string | null;
  senderSignature: string | null;
  clientMessageId: string | null;
  clientCreatedAt: number | null;
  createdAt: number;
  editedAt?: number | null;
  deletedAt?: number | null;
  contentUpdatedAt?: number | null;
  revision?: number;
  systemType: string | null;
  systemPayload: Record<string, unknown> | null;
  epoch?: number;
  localHistoryCiphertext?: LocalHistoryCiphertext | null;
};



export type StoredGroupKey = {
  id: string;
  chatId: string;
  epoch: number;
  rawKeyBase64: string;
  updatedAt: number;
};

export type StoredGroupParticipant = {
  id: string;
  chatId: string;
  identityId: string;
  publicKey: { algorithm: 'ed25519' | 'x25519'; value: string };
  joinOrder: number;
  joinedAt: number;
  isGuest: boolean;
  displayName: string | null;
  devices?: DeviceRecord[];
  updatedAt: number;
};
const DB_NAME = 'family-messenger-group-chats';
const DB_VERSION = 4;
const CHATS = 'group_chats';
const MESSAGES = 'group_messages';
const KEYS = 'group_keys';
const PARTICIPANTS = 'group_participants';
const MAX_KEYS_PER_CHAT = 8;
const OLD_EPOCH_KEY_GRACE_MS = 24 * 60 * 60 * 1000;
const NATIVE_GROUP_MIGRATION_KEY = 'circlus.nativeChatStorage.groupMessagesMigrated.v1';
let groupNativeMigrationStarted = false;

function isNativeGroupStoragePrimary(): boolean {
  if (!canUseNativeChatStorage()) return false;
  try {
    return localStorage.getItem(NATIVE_GROUP_MIGRATION_KEY) === '1';
  } catch {
    return false;
  }
}

function markNativeGroupStoragePrimary(): void {
  try {
    localStorage.setItem(NATIVE_GROUP_MIGRATION_KEY, '1');
  } catch {
    // Keep IndexedDB as primary when persistent local flagging is unavailable.
  }
}

function scheduleGroupNativeMigration(): void {
  if (!canUseNativeChatStorage() || isNativeGroupStoragePrimary() || groupNativeMigrationStarted) return;
  groupNativeMigrationStarted = true;
  window.setTimeout(() => {
    void migrateGroupStorageToNative()
      .catch(() => undefined)
      .finally(() => {
        groupNativeMigrationStarted = false;
      });
  }, 250);
}

function parseNativeArray<T>(raw: string | null): T[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : null;
  } catch {
    return null;
  }
}

function parseNativeObject<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function openDb(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db, request, event) => {
    if (!db.objectStoreNames.contains(CHATS)) {
      const store = db.createObjectStore(CHATS, { keyPath: 'chatId' });
      store.createIndex('byUpdatedAt', 'updatedAt');
    }
    if (!db.objectStoreNames.contains(MESSAGES)) {
      const store = db.createObjectStore(MESSAGES, { keyPath: 'messageId' });
      store.createIndex('byChatCreated', ['chatId', 'createdAt']);
    }
    if (!db.objectStoreNames.contains(KEYS)) {
      const store = db.createObjectStore(KEYS, { keyPath: 'id' });
      store.createIndex('byChatEpoch', ['chatId', 'epoch'], { unique: true });
    }
    if (!db.objectStoreNames.contains(PARTICIPANTS)) {
      const store = db.createObjectStore(PARTICIPANTS, { keyPath: 'id' });
      store.createIndex('byChat', 'chatId');
    }
    if ((event?.oldVersion || 0) < 4 && db.objectStoreNames.contains(MESSAGES)) {
      const tx = request.transaction;
      const store = tx?.objectStore(MESSAGES);
      const cursorReq = store?.openCursor();
      if (cursorReq) {
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return;
          const value = cursor.value as StoredGroupMessage;
          if (value.localHistoryCiphertext && !value.deletedAt) {
            cursor.update({ ...value, ciphertext: null });
          }
          cursor.continue();
        };
      }
    }
  });
}

async function readAllGroupChatsFromIndexedDb(): Promise<StoredGroupChat[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(CHATS, 'readonly');
    return await requestToPromise(tx.objectStore(CHATS).getAll()) as StoredGroupChat[];
  } finally {
    db.close();
  }
}

async function readAllGroupMessagesFromIndexedDb(): Promise<StoredGroupMessage[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(MESSAGES, 'readonly');
    return await requestToPromise(tx.objectStore(MESSAGES).getAll()) as StoredGroupMessage[];
  } finally {
    db.close();
  }
}

function getGroupMessageCreatedAt(message: StoredGroupMessage): number {
  return Number(message.createdAt || 0);
}

function isGroupMessageInCreatedRange(
  message: StoredGroupMessage,
  startInclusive: number,
  endExclusive: number
): boolean {
  const createdAt = getGroupMessageCreatedAt(message);
  return Number.isFinite(createdAt) && createdAt >= startInclusive && createdAt < endExclusive;
}

export async function getGroupMessagesCreatedInRange(
  startInclusive: number,
  endExclusive: number
): Promise<StoredGroupMessage[]> {
  if (isNativeGroupStoragePrimary()) {
    const chats = parseNativeArray<StoredGroupChat>(nativeChatStorage.getGroupChats());
    if (chats) {
      const rows: StoredGroupMessage[] = [];
      for (const chat of chats) {
        const messages = parseNativeArray<StoredGroupMessage>(nativeChatStorage.getGroupMessages(chat.chatId));
        if (!messages) continue;
        rows.push(...messages.filter((message) => isGroupMessageInCreatedRange(message, startInclusive, endExclusive)));
      }
      rows.sort((a, b) => getGroupMessageCreatedAt(a) - getGroupMessageCreatedAt(b));
      return rows;
    }
  } else {
    scheduleGroupNativeMigration();
  }

  const rows = await readAllGroupMessagesFromIndexedDb();
  return rows
    .filter((message) => isGroupMessageInCreatedRange(message, startInclusive, endExclusive))
    .sort((a, b) => getGroupMessageCreatedAt(a) - getGroupMessageCreatedAt(b));
}

async function readAllGroupParticipantsFromIndexedDb(): Promise<StoredGroupParticipant[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(PARTICIPANTS, 'readonly');
    return await requestToPromise(tx.objectStore(PARTICIPANTS).getAll()) as StoredGroupParticipant[];
  } finally {
    db.close();
  }
}

async function clearGroupStorageIndexedDb(): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction([CHATS, MESSAGES, PARTICIPANTS], 'readwrite');
    tx.objectStore(CHATS).clear();
    tx.objectStore(MESSAGES).clear();
    tx.objectStore(PARTICIPANTS).clear();
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function migrateGroupStorageToNative(): Promise<void> {
  if (!canUseNativeChatStorage() || isNativeGroupStoragePrimary()) return;
  const [chats, messages, participants] = await Promise.all([
    readAllGroupChatsFromIndexedDb(),
    readAllGroupMessagesFromIndexedDb(),
    readAllGroupParticipantsFromIndexedDb()
  ]);
  if (chats.length === 0 && messages.length === 0 && participants.length === 0) {
    markNativeGroupStoragePrimary();
    window.dispatchEvent(new Event('group-chats:updated'));
    return;
  }
  const participantsByChat = new Map<string, StoredGroupParticipant[]>();
  for (const row of participants) {
    const list = participantsByChat.get(row.chatId) || [];
    list.push(row);
    participantsByChat.set(row.chatId, list);
  }
  if (!nativeChatStorage.clearGroupChats()) return;
  if (!nativeChatStorage.clearGroupMessages()) return;
  if (!nativeChatStorage.clearGroupParticipants()) return;
  if (chats.length > 0 && !nativeChatStorage.saveGroupChats(JSON.stringify(chats))) return;
  if (messages.length > 0 && !nativeChatStorage.saveGroupMessages(JSON.stringify(messages))) return;
  for (const [chatId, rows] of participantsByChat) {
    if (!nativeChatStorage.saveGroupParticipants(chatId, JSON.stringify(rows))) return;
  }
  await clearGroupStorageIndexedDb();
  markNativeGroupStoragePrimary();
  window.dispatchEvent(new Event('group-chats:updated'));
}

export async function saveGroupChats(chats: StoredGroupChat[]): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.saveGroupChats(JSON.stringify(chats));
    if (isNativeGroupStoragePrimary()) {
      window.dispatchEvent(new Event('group-chats:updated'));
      return;
    }
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(CHATS, 'readwrite');
    const store = tx.objectStore(CHATS);
    for (const chat of chats) store.put(chat);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function getGroupChats(): Promise<StoredGroupChat[]> {
  if (isNativeGroupStoragePrimary()) {
    const rows = parseNativeArray<StoredGroupChat>(nativeChatStorage.getGroupChats());
    if (rows) {
      rows.sort((a, b) => (b.lastMessageAt || b.updatedAt) - (a.lastMessageAt || a.updatedAt));
      return rows;
    }
  } else {
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(CHATS, 'readonly');
    const store = tx.objectStore(CHATS);
    const rows = await requestToPromise(store.getAll()) as StoredGroupChat[];
    rows.sort((a, b) => (b.lastMessageAt || b.updatedAt) - (a.lastMessageAt || a.updatedAt));
    return rows;
  } finally {
    db.close();
  }
}

export async function countUnreadGroupChatsForIdentities(identityIds: string[]): Promise<number> {
  const localSet = new Set(identityIds.map((id) => (id || '').trim()).filter(Boolean));
  if (localSet.size === 0) return 0;
  const chats = await getGroupChats();
  return chats.reduce((count, chat) => {
    const localIdentityId = (chat.localIdentityId || '').trim();
    if (!localIdentityId || !localSet.has(localIdentityId)) return count;
    return Number(chat.unreadCount || 0) > 0 ? count + 1 : count;
  }, 0);
}

export async function saveGroupMessages(messages: StoredGroupMessage[]): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.saveGroupMessages(JSON.stringify(messages));
    if (isNativeGroupStoragePrimary()) {
      window.dispatchEvent(new Event('group-chats:updated'));
      return;
    }
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(MESSAGES, 'readwrite');
    const store = tx.objectStore(MESSAGES);
    for (const msg of messages) {
      store.put(
        msg.localHistoryCiphertext && !msg.deletedAt
          ? { ...msg, ciphertext: null }
          : msg
      );
    }
    await transactionDone(tx);
    window.dispatchEvent(new Event('group-chats:updated'));
  } finally {
    db.close();
  }
}

export async function getGroupMessages(chatId: string): Promise<StoredGroupMessage[]> {
  if (isNativeGroupStoragePrimary()) {
    const rows = parseNativeArray<StoredGroupMessage>(nativeChatStorage.getGroupMessages(chatId));
    if (rows) {
      rows.sort((a, b) => a.createdAt - b.createdAt);
      return rows;
    }
  } else {
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(MESSAGES, 'readonly');
    const store = tx.objectStore(MESSAGES);
    const index = store.index('byChatCreated');
    const rows = await requestToPromise(
      index.getAll(IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER]))
    ) as StoredGroupMessage[];
    rows.sort((a, b) => a.createdAt - b.createdAt);
    return rows;
  } finally {
    db.close();
  }
}

export async function getLatestGroupMessage(chatId: string): Promise<StoredGroupMessage | null> {
  if (isNativeGroupStoragePrimary()) {
    const row = parseNativeObject<StoredGroupMessage>(nativeChatStorage.getLatestGroupMessage(chatId));
    if (row) return row;
  } else {
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(MESSAGES, 'readonly');
    const store = tx.objectStore(MESSAGES);
    const index = store.index('byChatCreated');
    const range = IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER]);
    const cursorReq = index.openCursor(range, 'prev');
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result as IDBCursorWithValue | null;
      const row = (cursor?.value || null) as StoredGroupMessage | null;
      db.close();
      resolve(row);
    };
    cursorReq.onerror = () => { db.close(); reject(cursorReq.error); };
  });
}

export async function deleteGroupMessages(chatId: string): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.deleteGroupMessages(chatId);
    if (isNativeGroupStoragePrimary()) {
      window.dispatchEvent(new Event('group-chats:updated'));
      return;
    }
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(MESSAGES, 'readwrite');
    const store = tx.objectStore(MESSAGES);
    const index = store.index('byChatCreated');
    const range = IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER]);
    const cursorReq = index.openCursor(range);

    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result as IDBCursorWithValue | null;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
    tx.oncomplete = () => {
      window.dispatchEvent(new Event('group-chats:updated'));
      db.close();
      resolve();
    };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function deleteGroupChatLocalData(chatId: string, keyEpoch: number): Promise<void> {
  nativeChatStorage.deleteGroupChat(chatId);
  nativeChatStorage.deleteGroupParticipants(chatId);
  await deleteGroupMessages(chatId);
  const db = await openDb();
  try {
    const tx = db.transaction([CHATS, PARTICIPANTS], 'readwrite');
    tx.objectStore(CHATS).delete(chatId);
    const participantKeys = await requestToPromise(tx.objectStore(PARTICIPANTS).index('byChat').getAllKeys(chatId)) as IDBValidKey[];
    for (const key of participantKeys) tx.objectStore(PARTICIPANTS).delete(key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
  for (let epoch = 1; epoch <= Math.max(1, keyEpoch); epoch += 1) {
    await deleteGroupChatKey(chatId, epoch);
  }
  window.dispatchEvent(new Event('group-chats:updated'));
}

export async function deleteGroupMessage(messageId: string): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.deleteGroupMessage(messageId);
    if (isNativeGroupStoragePrimary()) {
      window.dispatchEvent(new Event('group-chats:updated'));
      return;
    }
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(MESSAGES, 'readwrite');
    tx.objectStore(MESSAGES).delete(messageId);
    await transactionDone(tx);
    window.dispatchEvent(new Event('group-chats:updated'));
  } finally {
    db.close();
  }
}




async function pruneGroupChatKeys(db: IDBDatabase, chatId: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(KEYS, 'readwrite');
    const store = tx.objectStore(KEYS);
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = (req.result || []) as StoredGroupKey[];
      const keysForChat = rows
        .filter((row) => row.chatId === chatId)
        .sort((a, b) => b.epoch - a.epoch);
      for (const row of keysForChat.slice(MAX_KEYS_PER_CHAT)) {
        store.delete(row.id);
      }
      const newestEpoch = keysForChat[0]?.epoch || 0;
      const cutoff = Date.now() - OLD_EPOCH_KEY_GRACE_MS;
      for (const row of keysForChat) {
        if (row.epoch < newestEpoch && row.updatedAt < cutoff) {
          store.delete(row.id);
        }
      }
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function saveGroupChatKeyToIndexedDb(chatId: string, epoch: number, rawKeyBase64: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(KEYS, 'readwrite');
    const store = tx.objectStore(KEYS);
    store.put({ id: `${chatId}:${epoch}`, chatId, epoch, rawKeyBase64, updatedAt: Date.now() } as StoredGroupKey);
    await transactionDone(tx);
  } finally {
    db.close();
  }
  const db2 = await openDb();
  try {
    await pruneGroupChatKeys(db2, chatId);
  } finally {
    db2.close();
  }
}

async function getGroupChatKeyFromIndexedDb(chatId: string, epoch: number): Promise<string | null> {
  const db = await openDb();
  try {
    await pruneGroupChatKeys(db, chatId);
    const tx = db.transaction(KEYS, 'readonly');
    const store = tx.objectStore(KEYS);
    const result = await requestToPromise(store.get(`${chatId}:${epoch}`)) as StoredGroupKey | undefined;
    return result?.rawKeyBase64 || null;
  } finally {
    db.close();
  }
}

async function deleteGroupChatKeyFromIndexedDb(chatId: string, epoch: number): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(KEYS, 'readwrite');
    tx.objectStore(KEYS).delete(`${chatId}:${epoch}`);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function saveGroupChatKey(chatId: string, epoch: number, rawKeyBase64: string): Promise<void> {
  if (saveNativeChatEpochKey('group', chatId, epoch, rawKeyBase64)) {
    await deleteGroupChatKeyFromIndexedDb(chatId, epoch);
    return;
  }
  await saveGroupChatKeyToIndexedDb(chatId, epoch, rawKeyBase64);
}

export async function getGroupChatKey(chatId: string, epoch: number): Promise<string | null> {
  const nativeKey = getNativeChatEpochKey('group', chatId, epoch);
  if (nativeKey) return nativeKey;

  const indexedDbKey = await getGroupChatKeyFromIndexedDb(chatId, epoch);
  if (indexedDbKey && canUseNativeChatEpochKeyStore() && saveNativeChatEpochKey('group', chatId, epoch, indexedDbKey)) {
    await deleteGroupChatKeyFromIndexedDb(chatId, epoch);
  }
  return indexedDbKey;
}

export async function deleteGroupChatKey(chatId: string, epoch: number): Promise<void> {
  deleteNativeChatEpochKey('group', chatId, epoch);
  await deleteGroupChatKeyFromIndexedDb(chatId, epoch);
}

export async function saveGroupParticipants(
  chatId: string,
  participants: Omit<StoredGroupParticipant, 'id' | 'chatId' | 'updatedAt'>[]
): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.saveGroupParticipants(chatId, JSON.stringify(participants));
    if (isNativeGroupStoragePrimary()) return;
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(PARTICIPANTS, 'readwrite');
    const store = tx.objectStore(PARTICIPANTS);
    const index = store.index('byChat');
    const existing = await requestToPromise(index.getAllKeys(chatId)) as IDBValidKey[];
    for (const key of existing) store.delete(key);
    const updatedAt = Date.now();
    for (const participant of participants) {
      store.put({
        ...participant,
        id: `${chatId}:${participant.identityId}`,
        chatId,
        updatedAt
      } satisfies StoredGroupParticipant);
    }
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function getGroupParticipants(chatId: string): Promise<StoredGroupParticipant[]> {
  if (isNativeGroupStoragePrimary()) {
    const rows = parseNativeArray<StoredGroupParticipant>(nativeChatStorage.getGroupParticipants(chatId));
    if (rows) {
      rows.sort((a, b) => a.joinOrder - b.joinOrder || a.joinedAt - b.joinedAt);
      return rows;
    }
  } else {
    scheduleGroupNativeMigration();
  }
  const db = await openDb();
  try {
    const tx = db.transaction(PARTICIPANTS, 'readonly');
    const store = tx.objectStore(PARTICIPANTS);
    const index = store.index('byChat');
    const rows = await requestToPromise(index.getAll(chatId)) as StoredGroupParticipant[];
    rows.sort((a, b) => a.joinOrder - b.joinOrder || a.joinedAt - b.joinedAt);
    return rows;
  } finally {
    db.close();
  }
}
